import React from 'react';

type ClickHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;

interface UseMultiClickDetectionProps {
  onDoubleClick?: ClickHandler;
  onTripleClick?: ClickHandler;
  delay?: number;
}

export const useMultiClickDetection = ({
  onDoubleClick,
  onTripleClick,
  delay = 200,
}: UseMultiClickDetectionProps) => {
  const doubleClickTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventRef = React.useRef<React.MouseEvent<HTMLElement, MouseEvent> | null>(null);

  const handleDoubleClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      lastEventRef.current = e;

      if (doubleClickTimeoutRef.current) {
        clearTimeout(doubleClickTimeoutRef.current);
      }

      doubleClickTimeoutRef.current = setTimeout(() => {
        if (onDoubleClick) {
          onDoubleClick(e);
        }

        lastEventRef.current = null;
      }, delay);
    },
    [onDoubleClick, delay],
  );

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const clickCount = e.detail;

      if (clickCount === 3 && onTripleClick) {
        if (doubleClickTimeoutRef.current) {
          clearTimeout(doubleClickTimeoutRef.current);
          doubleClickTimeoutRef.current = null;
        }

        onTripleClick(e);
      }
    },
    [onTripleClick],
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (doubleClickTimeoutRef.current) {
        clearTimeout(doubleClickTimeoutRef.current);
      }
    };
  }, []);

  return { handleClick, handleDoubleClick };
};

export function withMultiClickDetection<
  P extends {
    onDoubleClick?: ClickHandler;
    onClick?: ClickHandler;
  },
>(WrappedComponent: React.ComponentType<P & { onTripleClick?: ClickHandler }>) {
  return function MultiClickAreaWithLogger(props: P & { onTripleClick?: ClickHandler }) {
    const { onDoubleClick, onClick, onTripleClick, ...rest } = props;

    const {
      handleClick: handleClickForTripleDetection,
      handleDoubleClick: handleClickWaitingForTriple,
    } = useMultiClickDetection({
      onDoubleClick,
      onTripleClick,
    });

    const onClickCallback = React.useCallback(
      (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleClickForTripleDetection(e);
        onClick?.(e);
      },
      [handleClickForTripleDetection, onClick],
    );

    return (
      <WrappedComponent
        {...(rest as P)}
        onClick={onClickCallback}
        onDoubleClick={handleClickWaitingForTriple}
      />
    );
  };
}
